/**
 * ListeningPage.js
 *
 * Handles the Listening section of the mock test, including both practice and simulation modes.
 * Optimized for readability, maintainability, and performance.
 * Senior-level comments and structure for future maintainers.
 */

import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { showNotification } from "../../common/headerSlice";
import {
  useLazyGetPracticeTestDataQuery,
  useSubmitTestAnswersMutation,
  useLazyGetTestAttemptResultsQuery,
  useUpdateTestAttemptMutation,
} from "../mockTestSlice";
import { useLazyGetBundleTestDataQuery } from "../../bundles/bundleSlice";
import ListeningSection from "./ListeningSection";
import TestResultsPopup from "./TestResultsPopup";
import { getStoredTimerData, storeTimerData } from "../utils/practiceTimer";
import {
  InstructionWrapper,
  useInstructions,
} from "../../../components/Instructions";
import { useTestNavigation } from "../utils/testNavigation";

/* --------------------- UTILITY HELPERS --------------------- */
// Cleans heading by collapsing whitespace and formatting for display.
function cleanHeading(h = "") {
  if (h == null) return "";
  return String(h)
    .replace(/\r?\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s{2,}/g, " ")
    .replace(/\s+-\s+/g, " - ")
    .replace(/\s{1,}/g, " ")
    .replace(/\s{2,}/g, " ");
}

// Removes "(n)" tokens shown in question text (shown separately elsewhere)
function stripEmbeddedNum(text = "", qid) {
  if (text == null || qid == null) return text || "";
  const rx = new RegExp(`\\(\\s*${qid}\\s*\\)`, "g");
  return text.replace(rx, "").trim();
}

// Converts wide underscores to a single marker for blank width detection
function underscoreTokenize(text = "") {
  if (text == null) return "";
  return text.replace(/_{2,}/g, "___");
}

/* --------------------- MAIN COMPONENT --------------------- */
export default function ListeningPage({ isBundle = false }) {
  // ---- ROUTER, REDUX, AND INSTRUCTION HOOKS ----
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const audioRef = useRef(null);

  // Get navigation utilities for test type
  const { baseRoute, backRoute, getTestUrl, testType } = useTestNavigation();

  // Cleanup audio when component unmounts (navigating away from listening)
  useEffect(() => {
    return () => {
      // Pause and reset audio when leaving the listening page
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  // Instruction modal and dialog controls
  const {
    instructionState,
    showInstructionModal,
    hideInstructionModal,
    showTimeExpiryDialog,
    hideTimeExpiryDialog,
    showConfirmationDialog,
    hideConfirmationDialog,
  } = useInstructions();

  // ---- STATE MANAGEMENT ----
  const [showInitialInstructions, setShowInitialInstructions] = useState(true);
  const [listenScreen, setListenScreen] = useState(0);
  const [answers, setAnswers] = useState({});
  const [localListeningData, setLocalListeningData] = useState(null);
  const [currentExtractIndex, setCurrentExtractIndex] = useState(0);
  const [studentAnswerId, setStudentAnswerId] = useState(""); // For API call chaining

  // Notes state - persists across parts/extracts until test completion
  const [notes, setNotes] = useState([]);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  // Check for essential state and redirect if missing (browser refresh)
  const studentAttemptId = location.state?.studentAttemptId;
  useEffect(() => {
    if (!studentAttemptId || !location.state) {
      console.log(
        "Essential state missing, redirecting to main mock test screen"
      );
      navigate(getTestUrl(testId), { replace: true });
    }
  }, [studentAttemptId, location.state, navigate, testId, getTestUrl]);

  // Static part order for fallback navigation
  const PART_ORDER = ["A", "B", "C"];

  // ---- INITIAL PART LOGIC (Dynamic selection based on navigation state or API data) ----
  const initialPart = (() => {
    // Navigation state-based logic (practice/simulation modes)
    const partsOrder = location.state?.listeningPartsOrder;
    const partIdx = location.state?.currentListeningPartIndex || 0;
    const getApiPartLetter = (partId) => {
      const apiParts =
        location.state?.practiceData?.data?.listeningTest?.listeningParts;
      if (apiParts) {
        const found = apiParts.find((p) => p._id === partId);
        if (found && found.part) return found.part;
      }
      return null;
    };
    // Try practice mode order
    if (
      Array.isArray(partsOrder) &&
      partsOrder.length > 0 &&
      partIdx < partsOrder.length
    ) {
      const letter = getApiPartLetter(partsOrder[partIdx]);
      if (letter) return letter;
    }
    // Try simulation step
    const simStep = location.state?.simulationStep;
    if (
      simStep &&
      Array.isArray(simStep.listeningPartIds) &&
      typeof simStep.listeningPartIndex === "number"
    ) {
      const letter = getApiPartLetter(
        simStep.listeningPartIds[simStep.listeningPartIndex]
      );
      if (letter) return letter;
    }
    // Fallback to first available API part
    const apiParts =
      location.state?.practiceData?.data?.listeningTest?.listeningParts;
    if (apiParts?.length > 0 && apiParts[0].part) return apiParts[0].part;
    // Default to "A"
    return "A";
  })();
  const [currentPart, setCurrentPart] = useState(initialPart);

  // ---- API MUTATION HOOKS ----
  // Use bundle API when isBundle is true, otherwise use mock test API
  const [triggerGetMockPracticeData, { data: mockTestData, isLoading: mockLoading, error: mockError }] =
    useLazyGetPracticeTestDataQuery();
  const [triggerGetBundleData, { data: bundleTestData, isLoading: bundleLoading, error: bundleError }] =
    useLazyGetBundleTestDataQuery();
  
  // Use the appropriate data based on isBundle
  const triggerGetPracticeData = isBundle ? triggerGetBundleData : triggerGetMockPracticeData;
  const testData = isBundle ? bundleTestData : mockTestData;
  const isLoading = isBundle ? bundleLoading : mockLoading;
  const error = isBundle ? bundleError : mockError;

  const [submitTestAnswers] = useSubmitTestAnswersMutation();
  const [getTestAttemptResults] = useLazyGetTestAttemptResultsQuery();
  const [updateTestAttempt] = useUpdateTestAttemptMutation();

  // Results state for popup
  const [testResults, setTestResults] = useState(null);
  const [showResultsPopup, setShowResultsPopup] = useState(false);

  // ---- TIMER (Practice Mode) ----
  const timerMinutes = location.state?.timerMinutes;
  const apiStartTime = location.state?.mockTest?.testStartTime;
  const apiEndTime = location.state?.mockTest?.testEndDate;
  const testAttemptId = location.state?.mockTest?.studentAttemptId;
  const storedTimer = getStoredTimerData(testAttemptId);

  const [practiceTimerStartTime, setPracticeTimerStartTime] = useState(
    apiStartTime || storedTimer?.startTime || null
  );
  const [practiceTimerEndTime, setPracticeTimerEndTime] = useState(
    apiEndTime || storedTimer?.endTime || null
  );

  // ---- INSTRUCTION MODAL & PART INSTRUCTION HANDLING ----
  const [lastPart, setLastPart] = useState(null);
  const instrOpen =
    instructionState.showInstructions &&
    instructionState.currentSection === "listening";

  // ---- EFFECTS ----

  // Show initial instructions modal at section start (simulation/practice)
  useEffect(() => {
    if (showInitialInstructions) {
      const mode = location.state?.isPracticeMode ? "practice" : "simulation";
      showInstructionModal("listening", null, mode);
      setShowInitialInstructions(false);
    }
  }, [showInitialInstructions, showInstructionModal, location.state]);

  // Show part-specific instructions on part change
  useEffect(() => {
    if (!currentPart) return;
    const availableParts =
      localListeningData?.data?.listeningTest?.listeningParts?.map(
        (p) => p.part
      );
    if (availableParts?.length === 1 && currentPart !== availableParts[0])
      return;
    const mode = location.state?.isPracticeMode ? "practice" : "simulation";
    if (currentPart && currentPart !== lastPart) {
      showInstructionModal("listening", currentPart, mode);
      setLastPart(currentPart);
    }
  }, [
    location.state,
    currentPart,
    lastPart,
    showInstructionModal,
    localListeningData,
  ]);

  // Store timer data whenever start/end time changes
  useEffect(() => {
    if (practiceTimerStartTime && practiceTimerEndTime) {
      storeTimerData(
        practiceTimerStartTime,
        practiceTimerEndTime,
        testAttemptId
      );
    }
  }, [practiceTimerStartTime, practiceTimerEndTime, testAttemptId]);

  // Practice timer initialization logic
  useEffect(() => {
    const isPracticeModeLocal = location.state?.isPracticeMode;

    // For listening in practice mode, we want audio-based timing
    if (isPracticeModeLocal) {
      console.log("Listening practice mode - using audio-based timing");
      // Set endTime to null to indicate to ListeningSection that it should use audio-based timing
      setPracticeTimerEndTime(null);
      // Still set a startTime for reference
      if (!practiceTimerStartTime) {
        const now = new Date();
        setPracticeTimerStartTime(now.toISOString());
      }
      return;
    }

    // For simulation mode, use API timer as before
    // Priority: 1. API timer, 2. stored timer, 3. create new timer
    if (apiStartTime && apiEndTime && !practiceTimerStartTime) {
      setPracticeTimerStartTime(apiStartTime);
      setPracticeTimerEndTime(apiEndTime);
    } else if (
      storedTimer &&
      storedTimer.startTime &&
      storedTimer.endTime &&
      !practiceTimerStartTime
    ) {
      setPracticeTimerStartTime(storedTimer.startTime);
      setPracticeTimerEndTime(storedTimer.endTime);
    } else if (
      timerMinutes &&
      !practiceTimerStartTime &&
      !apiStartTime &&
      !storedTimer
    ) {
      const now = new Date();
      const endTime = new Date(now.getTime() + timerMinutes * 60 * 1000);
      setPracticeTimerStartTime(now.toISOString());
      setPracticeTimerEndTime(endTime.toISOString());
    }
  }, [
    location.state?.isPracticeMode,
    timerMinutes,
    practiceTimerStartTime,
    practiceTimerEndTime,
    apiStartTime,
    apiEndTime,
    storedTimer,
  ]);

  // Main test data fetch logic, handles both practice and simulation modes
  useEffect(() => {
    const practiceDataFromState = location.state?.practiceData;
    if (practiceDataFromState) {
      setLocalListeningData(practiceDataFromState);
      return;
    }

    const studentAttemptId = location.state?.studentAttemptId;
    const isPracticeMode = location.state?.isPracticeMode;
    // PRACTICE MODE: fetch current part
    if (studentAttemptId && isPracticeMode) {
      const listeningPartsOrder = location.state?.listeningPartsOrder || [];
      const currentListeningPartIndex =
        location.state?.currentListeningPartIndex || 0;
      const currentPartId = listeningPartsOrder[currentListeningPartIndex];
      if (currentPartId) {
        triggerGetPracticeData({
          studentAttemptId,
          currentSection: "listening",
          listeningId: location.state?.listeningTestId,
          listeningPartIds: [currentPartId],
        });
      } else {
        triggerGetPracticeData({
          studentAttemptId,
          currentSection: "listening",
          listeningId: location.state?.listeningTestId,
          listeningPartIds: location.state?.listeningPartIds,
        });
      }
      return;
    }
    // SIMULATION MODE: fetch by simulationStep
    if (studentAttemptId && location.state?.simulationStep) {
      const simulationStep = location.state.simulationStep;
      const listeningPartIds = simulationStep.listeningPartIds || [];
      const listeningPartIndex = simulationStep.listeningPartIndex || 0;
      const partId = listeningPartIds[listeningPartIndex];
      if (partId) {
        triggerGetPracticeData({
          studentAttemptId,
          currentSection: "listening",
          listeningId: location.state?.listeningTestId,
          listeningPartIds: [partId],
        });
        return;
      }
    }
    // GENERIC FALLBACK: fetch current part and extract index
    if (studentAttemptId) {
      triggerGetPracticeData({
        studentAttemptId,
        currentPart,
        currentExtract: currentExtractIndex,
        isNextPart: true,
      });
    } else {
      // No studentAttemptId, can't continue
    }
  }, [
    testId,
    location.state?.studentAttemptId,
    location.state?.practiceData,
    location.state?.currentListeningPartIndex,
    location.state?.isPracticeMode,
    location.state?.listeningTestId,
    location.state?.listeningPartIds,
    location.state?.listeningPartsOrder,
    location.state?.simulationStep,
    currentPart,
    currentExtractIndex,
    triggerGetPracticeData,
  ]);

  // Sync simulationStep to part state
  useEffect(() => {
    if (
      location.state?.simulationStep &&
      localListeningData?.data?.listeningTest?.listeningParts
    ) {
      const { listeningPartIds, listeningPartIndex } =
        location.state.simulationStep;
      if (
        Array.isArray(listeningPartIds) &&
        typeof listeningPartIndex === "number" &&
        listeningPartIds.length > listeningPartIndex
      ) {
        const apiParts = localListeningData.data.listeningTest.listeningParts;
        const partObj = apiParts.find(
          (p) => p._id === listeningPartIds[listeningPartIndex]
        );
        const thisPart = partObj?.part || "A";
        setCurrentPart(thisPart);
        setCurrentExtractIndex(0);
        setListenScreen(0);
        setAnswers({});
      }
    }
  }, [location.state?.simulationStep, localListeningData]);

  // On new API data, reset to first part in response (practice mode)
  useEffect(() => {
    if (testData) {
      setLocalListeningData(testData);
      const isPracticeMode = location.state?.isPracticeMode;
      if (isPracticeMode && testData?.data?.listeningTest?.listeningParts) {
        const availableParts = testData.data.listeningTest.listeningParts;
        if (availableParts.length > 0) {
          // Validate that currentPart is a valid letter, if not set it from API
          const validPartLetters = ["A", "B", "C"];
          const isValidPartLetter = validPartLetters.includes(currentPart);
          const partExistsInData = availableParts.some((p) => p.part === currentPart);
          
          if (!isValidPartLetter || !partExistsInData) {
            console.log("Setting currentPart from API response:", availableParts[0].part, 
              "Previous value was:", currentPart);
            setCurrentPart(availableParts[0].part);
            setCurrentExtractIndex(0);
            setListenScreen(0);
            setAnswers({});
          }
        }
      }
    }
  }, [testData, location.state?.isPracticeMode, currentPart]);

  // ---- AUTO-NEXT/CONTINUE HANDLERS ----
  // Handles auto-next button logic, proceeds directly like reading section
  const handleAutoNext = async () => {
    await proceedAutoNext();
  };

  // Proceeds to next part/section, submitting answers as needed
  const proceedAutoNext = async () => {
    const studentAttemptId = location.state?.studentAttemptId;
    try {
      // --- ALWAYS SUBMIT ANSWERS (even if empty) ---
      let listeningTestId = testId;
      if (localListeningData?.data?.listeningTest?._id) {
        listeningTestId = localListeningData.data.listeningTest._id;
      }
      // Helper to send MCQ (optionKey) if needed
      const getMcqOptionKey = (questionId, givenAnswer) => {
        console.log(
          `[DEBUG] getMcqOptionKey called for questionId: ${questionId}, givenAnswer: "${givenAnswer}"`
        );

        // First search in transformed data structure
        const currentListeningData = transformListeningData(localListeningData);
        const searchIn = [];
        if (currentListeningData?.partB?.extracts)
          searchIn.push(...currentListeningData.partB.extracts);
        if (currentListeningData?.partC?.extracts)
          searchIn.push(...currentListeningData.partC.extracts);

        console.log(
          `[DEBUG] Searching in ${searchIn.length} extracts from transformed data`
        );

        for (const extract of searchIn) {
          if (extract.questions) {
            for (const qWrap of extract.questions) {
              const q = qWrap.content;
              if (
                String(q.questionId) === String(questionId) &&
                Array.isArray(q.options)
              ) {
                console.log(
                  `[DEBUG] Found question ${questionId} in transformed data with ${q.options.length} options`
                );
                console.log(`[DEBUG] Question options:`, q.options);
                const foundOption = q.options.find((opt, idx) => {
                  const optKey = opt?.key ?? String.fromCharCode(65 + idx); // A, B, C, etc.
                  return (
                    opt.value === givenAnswer ||
                    opt.label === givenAnswer ||
                    opt.key === givenAnswer ||
                    optKey === givenAnswer ||
                    opt.text === givenAnswer
                  );
                });
                if (foundOption) {
                  console.log(
                    `[DEBUG] Found option:`,
                    foundOption,
                    `for answer: ${givenAnswer}`
                  );
                  // Return the option's key if it exists, otherwise return the original answer
                  const optionKey = foundOption.key || givenAnswer;
                  console.log(`[DEBUG] Using option key: ${optionKey}`);
                  return optionKey;
                }
              }
            }
          }
        }

        // Also search directly in API data structure for Part C
        const apiParts =
          localListeningData?.data?.listeningTest?.listeningParts || [];
        console.log(`[DEBUG] Searching in ${apiParts.length} API parts`);

        for (const part of apiParts) {
          if (part.part === "C" && part.listeningExtract) {
            console.log(
              `[DEBUG] Found Part C with ${part.listeningExtract.length} extracts`
            );
            for (const extract of part.listeningExtract) {
              if (extract.extractQuestion) {
                console.log(
                  `[DEBUG] Extract has ${extract.extractQuestion.length} questions`
                );
                for (const question of extract.extractQuestion) {
                  if (
                    String(question.questionId) === String(questionId) &&
                    Array.isArray(question.options)
                  ) {
                    console.log(
                      `[DEBUG] Found Part C question ${questionId} with ${question.options.length} options`
                    );
                    console.log(
                      `[DEBUG] Part C question options:`,
                      question.options
                    );
                    const foundOption = question.options.find((opt, idx) => {
                      const optKey = opt?.key ?? String.fromCharCode(65 + idx); // A, B, C, etc.
                      return (
                        opt.value === givenAnswer ||
                        opt.label === givenAnswer ||
                        opt.key === givenAnswer ||
                        optKey === givenAnswer ||
                        opt.text === givenAnswer
                      );
                    });
                    if (foundOption) {
                      console.log(
                        `[DEBUG] Found Part C option:`,
                        foundOption,
                        `for answer: ${givenAnswer}`
                      );
                      // Return the option's key if it exists, otherwise return the original answer
                      const optionKey = foundOption.key || givenAnswer;
                      console.log(
                        `[DEBUG] Using Part C option key: ${optionKey}`
                      );
                      return optionKey;
                    } else {
                      console.log(
                        `[DEBUG] No matching option found for answer: ${givenAnswer}, available options:`,
                        question.options
                      );
                    }
                  }
                }
              }
            }
          }
        }

        console.log(
          `[DEBUG] No option key found, returning original answer: ${givenAnswer}`
        );
        return givenAnswer;
      };

      // Get all question IDs for the current part and build answers array
      const allQuestionIds = getAllQuestionIdsForCurrentPart();
      const submissionData = {
        testId: listeningTestId,
        testAttemptId: studentAttemptId,
        answers: allQuestionIds.map((questionId) => {
          const givenAnswer =
            answers[questionId] || answers[String(questionId)] || "";
          const processedAnswer =
            getMcqOptionKey(questionId, givenAnswer) || "";
          console.log(
            `[DEBUG] Processing answer for questionId ${questionId}: original="${givenAnswer}" -> processed="${processedAnswer}"`
          );
          return {
            questionId: parseInt(questionId) || questionId,
            givenAnswer: processedAnswer,
          };
        }),
        ...(studentAnswerId &&
          typeof studentAnswerId === "string" &&
          studentAnswerId.trim() !== "" && { studentAnswerId }),
        isNextPart: true,
      };
      console.log(`[DEBUG] Final submission data:`, submissionData);
      const response = await submitTestAnswers(submissionData).unwrap();
      const newStudentAnswerId =
        response?.studentAnswerId ||
        response?.data?.studentAnswerId ||
        response?.data?.data?.studentAnswerId;
      if (newStudentAnswerId) setStudentAnswerId(newStudentAnswerId);

      // --- NAVIGATION LOGIC (practice/simulation/other) ---
      const isPracticeMode = location.state?.isPracticeMode;
      const simulationStep = location.state?.simulationStep;

      if (isPracticeMode) {
        // PRACTICE MODE: move to next part or next section
        const listeningPartsOrder = location.state?.listeningPartsOrder || [];
        const currentListeningPartIndex =
          location.state?.currentListeningPartIndex || 0;
        const nextListeningPartIndex = currentListeningPartIndex + 1;
        if (nextListeningPartIndex < listeningPartsOrder.length) {
          try {
            const nextPartPracticeData = await triggerGetPracticeData({
              studentAttemptId,
              currentSection: "listening",
              listeningId: location.state?.listeningTestId,
              listeningPartIds: [listeningPartsOrder[nextListeningPartIndex]],
            }).unwrap();
            navigate(`${baseRoute}/${testId}/listening`, {
              state: {
                ...location.state,
                currentListeningPartIndex: nextListeningPartIndex,
                practiceData: nextPartPracticeData,
                studentAnswerId: studentAnswerId,
              },
            });
            return;
          } catch {
            // Fallback: ignore fetch error for next part
          }
        }
        const selectedSections = location.state?.selectedSections;
        if (isFinalSection()) {
          await showPracticeResults(studentAttemptId);
        } else if (selectedSections?.reading) {
          navigate(`${baseRoute}/${testId}/reading`, {
            state: { ...location.state, studentAnswerId, practiceData: null },
          });
        } else if (selectedSections?.writing) {
          navigate(`${baseRoute}/${testId}/writing`, {
            state: { ...location.state, studentAnswerId, practiceData: null },
          });
        } else {
          try {
            const resultsResponse = await getTestAttemptResults(
              studentAttemptId
            ).unwrap();
            setTestResults(resultsResponse);
            setShowResultsPopup(true);
          } catch {
            navigate(`${baseRoute}/${testId}/results`, {
              state: { ...location.state, studentAnswerId },
            });
          }
        }
      } else if (simulationStep) {
        // SIMULATION MODE: move to next part or reading/results
        const {
          listeningPartIds,
          listeningPartIndex,
          readingPartIds,
          totalReadingParts,
        } = simulationStep;
        const nextListeningPartIndex = (listeningPartIndex || 0) + 1;
        if (
          nextListeningPartIndex <
          (simulationStep.totalListeningParts || listeningPartIds.length)
        ) {
          setLocalListeningData(null);
          navigate(`${baseRoute}/${testId}/listening`, {
            state: {
              ...location.state,
              simulationStep: {
                ...simulationStep,
                listeningPartIndex: nextListeningPartIndex,
              },
              studentAnswerId: studentAnswerId,
              practiceData: null,
            },
          });
        } else {
          // All listening parts done - check if reading is available
          const hasReading =
            location.state?.mockTest?.oetTest?.readingId ||
            (readingPartIds && readingPartIds.length > 0);
          const hasWriting = location.state?.mockTest?.oetTest?.writingId;

          if (hasReading) {
            // Navigate to reading
            navigate(`${baseRoute}/${testId}/reading`, {
              state: {
                ...location.state,
                simulationStep: {
                  ...simulationStep,
                  readingPartIndex: 0,
                  totalReadingParts:
                    totalReadingParts || readingPartIds?.length || 0,
                  readingPartIds,
                  listeningCompleted: true,
                },
                studentAnswerId: studentAnswerId,
                practiceData: null,
              },
            });
          } else if (hasWriting) {
            // Navigate to writing
            navigate(`${baseRoute}/${testId}/writing`, {
              state: {
                ...location.state,
                simulationStep: {
                  ...simulationStep,
                  listeningCompleted: true,
                  currentSection: "writing",
                },
                studentAnswerId: studentAnswerId,
                practiceData: null,
              },
            });
          } else {
            // No reading or writing - this is the final section, show results
            console.log(
              "Simulation mode: Listening is last section, completing test and showing results"
            );
            await showTestResults(studentAttemptId, true);
          }
        }
      } else {
        // FALLBACK: move to next part in PART_ORDER or to reading
        const currentIdx = PART_ORDER.indexOf(currentPart);
        if (currentIdx !== -1 && currentIdx < PART_ORDER.length - 1) {
          setLocalListeningData(null);
          setCurrentPart(PART_ORDER[currentIdx + 1]);
          setCurrentExtractIndex(0);
          setListenScreen(0);
          setAnswers({});
        } else {
          navigate(`${baseRoute}/${testId}/reading`, {
            state: { ...location.state, studentAnswerId },
          });
        }
      }
    } catch (error) {
      dispatch(
        showNotification({
          message: "Error submitting answers. Please try again.",
          status: 0,
        })
      );
    }
  };

  // Handler for answer change in the UI
  const handleAnswerChange = (questionId, value) => {
    console.log(
      `[DEBUG] handleAnswerChange called - questionId: ${questionId}, value: "${value}"`
    );
    setAnswers((prev) => {
      const newAnswers = {
        ...prev,
        [questionId]: value,
      };
      console.log(`[DEBUG] Updated answers state:`, newAnswers);
      return newAnswers;
    });
  };

  // Handler for adding notes - persists across parts/extracts
  const handleAddNote = (noteData) => {
    const newNote = {
      selectedText: noteData.selectedText,
      userNote: noteData.userNote,
      timestamp: new Date().toLocaleTimeString(),
      id: Date.now(),
      part: currentPart, // Track which part the note was created in
      extractName:
        listeningData?.playlist?.[listenScreen]?.extractName ||
        `Part ${currentPart}`,
    };
    setNotes((prev) => [...prev, newNote]);
  };

  // Helper to get all question IDs for the current listening part
  const getAllQuestionIdsForCurrentPart = () => {
    const listeningParts =
      localListeningData?.data?.listeningTest?.listeningParts || [];
    const currentPartData = listeningParts.find((p) => p.part === currentPart);

    if (!currentPartData || !currentPartData.listeningExtract) {
      return [];
    }

    const questionIds = [];
    currentPartData.listeningExtract.forEach((extract) => {
      if (extract.extractQuestion) {
        extract.extractQuestion.forEach((q) => {
          if (q.questionId) {
            questionIds.push(q.questionId);
          }
        });
      }
    });

    return questionIds;
  };

  // Helper to determine if this is the last section in practice mode
  const isFinalSection = () => {
    const selectedSections = location.state?.selectedSections;
    if (!selectedSections || !selectedSections.listening) return false;
    return !selectedSections.reading && !selectedSections.writing;
  };

  // Helper to check if listening is the final section in simulation mode
  const isSimulationFinalSection = () => {
    const simulationStep = location.state?.simulationStep;
    if (!simulationStep) return false;

    // Check if reading is available
    const hasReading =
      location.state?.mockTest?.oetTest?.readingId ||
      (simulationStep.readingPartIds &&
        simulationStep.readingPartIds.length > 0);
    const hasWriting = location.state?.mockTest?.oetTest?.writingId;

    return !hasReading && !hasWriting;
  };

  // Helper function to show results (works for both practice and simulation mode)
  const showTestResults = async (
    studentAttemptId,
    shouldUpdateAttempt = false
  ) => {
    try {
      console.log(
        "Fetching test results for studentAttemptId:",
        studentAttemptId,
        "shouldUpdateAttempt:",
        shouldUpdateAttempt
      );

      // Mark test attempt as completed if requested (simulation mode)
      if (shouldUpdateAttempt) {
        try {
          console.log("Updating test attempt as completed...");
          await updateTestAttempt({
            studentAttemptId,
            isCompleted: true,
          }).unwrap();
          console.log("Test attempt marked as completed");
        } catch (updateError) {
          console.error("Error updating test attempt:", updateError);
          // Continue to fetch results even if update fails
        }
      }

      const resultsResponse = await getTestAttemptResults(
        studentAttemptId
      ).unwrap();
      console.log("Test results:", resultsResponse);
      setTestResults(resultsResponse);
      setShowResultsPopup(true);
    } catch (resultsError) {
      console.error("Error fetching test attempt results:", resultsError);
      dispatch(
        showNotification({
          message: "Error fetching results. Please try again.",
          status: 0,
        })
      );
    }
  };

  // Alias for backward compatibility
  const showPracticeResults = (studentAttemptId) =>
    showTestResults(studentAttemptId, false);

  // API -> UI adapter for ListeningSection (formats Part A/B/C structure)
  const transformListeningData = (apiData) => {
    if (!apiData?.data?.listeningTest?.listeningParts) return null;
    const listeningTest = apiData.data.listeningTest;
    const parts = listeningTest.listeningParts;

    // Formats an audio URL for CDN
    const formatAudioUrl = (audioUrl) => {
      if (!audioUrl) return null;
      let url = audioUrl;
      if (url.startsWith("https://api.mmitsol.com/"))
        url = url.replace("https://api.mmitsol.com/", "");
      if (url.startsWith("/")) url = url.substring(1);
      if (!url.startsWith("https://")) url = "https://" + url;
      return url;
    };

    const partA = parts.find((part) => part.part === "A");
    const partB = parts.find((part) => part.part === "B");
    const partC = parts.find((part) => part.part === "C");
    const result = {};

    // Format Part A: notes/sections structure
    if (partA) {
      result.partA = {
        extracts:
          partA?.listeningExtract?.map((extract) => {
            const questions = extract.extractQuestion || [];
            const groupedSections = {};
            questions.forEach((q) => {
              const heading = cleanHeading(q.heading || "General");
              if (!groupedSections[heading]) groupedSections[heading] = [];
              groupedSections[heading].push({
                question_id: q._id,
                questionId: q.questionId,
                question: underscoreTokenize(
                  stripEmbeddedNum(q.question, q.questionId)
                ),
                heading: q.heading,
              });
            });
            return {
              audioURL: formatAudioUrl(extract.audioUrl),
              instructions: extract.instructions,
              extractName: extract.extractName,
              questions: [
                {
                  content: {
                    instructions: extract.instructions,
                    heading: extract.extractName,
                    sections: Object.entries(groupedSections).map(
                      ([heading, notes]) => ({
                        heading,
                        notes,
                      })
                    ),
                  },
                },
              ],
            };
          }) || [],
      };
    }

    // Format Part B and C: MCQ/short-answer
    const formatBCPart = (part) =>
      part?.listeningExtract?.map((extract) => ({
        audioURL: formatAudioUrl(extract.audioUrl),
        instructions: extract.instructions,
        extractName: extract.extractName,
        questions:
          extract.extractQuestion?.map((q) => ({
            content: {
              question_id: q._id,
              questionId: q.questionId,
              question: q.question,
              options: q.options || [],
              questionType: q.questionType,
              questionNumber: q.questionNumber,
              instructions: q.instructions || extract.instructions,
            },
          })) || [],
      })) || [];

    if (partB) result.partB = { extracts: formatBCPart(partB) };
    if (partC) result.partC = { extracts: formatBCPart(partC) };
    return result;
  };

  /* --------------------- UI RENDERING LOGIC --------------------- */

  // Show loader if loading/waiting for data
  if (isLoading || !localListeningData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} thickness={5} />
        <Typography sx={{ ml: 2 }}>Loading listening data...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography color="error">
          Error loading listening test: {error.message}
        </Typography>
      </Box>
    );
  }

  // Transform API data for UI
  const listeningData = transformListeningData(localListeningData);

  // Show empty data fallback
  if (!listeningData && !isLoading && localListeningData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Typography>No listening data available</Typography>
      </Box>
    );
  }

  // Main UI
  return (
    <>
      {/* Instruction Modal/Dialogs */}
      <InstructionWrapper
        instructionState={instructionState}
        onInstructionNext={hideInstructionModal}
        onInstructionClose={hideInstructionModal}
        onConfirmationConfirm={() => {
          hideConfirmationDialog();
        }}
        onConfirmationClose={hideConfirmationDialog}
        onTimeExpiryContinue={hideTimeExpiryDialog}
        timeRemaining={null}
        nextSection="reading"
      />
      {/* Listening Section Main UI */}
      <ListeningSection
        key={`listening-${currentPart}-${
          location.state?.currentListeningPartIndex || 0
        }`}
        listeningData={listeningData}
        listenScreen={listenScreen}
        setListenScreen={setListenScreen}
        audioRef={audioRef}
        instrOpen={instrOpen}
        answers={answers}
        onAnswerChange={handleAnswerChange}
        onAutoNext={handleAutoNext}
        timer="no-limit"
        mode={location.state?.isPracticeMode ? "practice" : "simulation"}
        currentPart={currentPart}
        currentExtractIndex={currentExtractIndex}
        startTime={practiceTimerStartTime}
        endTime={practiceTimerEndTime}
        testId={testId}
        // Notes props - persistent across parts/extracts
        notes={notes}
        onAddNote={handleAddNote}
        notesModalOpen={notesModalOpen}
        setNotesModalOpen={setNotesModalOpen}
        onBack={() => {
          const sectionOrder = location.state?.sectionOrder || [];
          const currentSectionIndex = location.state?.currentSectionIndex || 0;
          if (currentSectionIndex > 0) {
            const previousSection = sectionOrder[currentSectionIndex - 1];
            navigate(`${baseRoute}/${testId}/${previousSection}`, {
              state: {
                ...location.state,
                currentSectionIndex: currentSectionIndex - 1,
              },
            });
          }
        }}
        isLastQuestion={
          // Practice mode: check if this is last section and last part
          (location.state?.isPracticeMode &&
            location.state?.sectionOrder &&
            location.state?.currentSectionIndex ===
              location.state?.sectionOrder.length - 1 &&
            (() => {
              const listeningPartsOrder =
                location.state?.listeningPartsOrder || [];
              const currentListeningPartIndex =
                location.state?.currentListeningPartIndex || 0;
              return (
                currentListeningPartIndex === listeningPartsOrder.length - 1
              );
            })()) ||
          // Simulation mode: check if listening is final section and on last part
          (location.state?.simulationStep &&
            isSimulationFinalSection() &&
            (() => {
              const simulationStep = location.state?.simulationStep;
              const listeningPartIds = simulationStep?.listeningPartIds || [];
              const currentPartIndex = simulationStep?.listeningPartIndex || 0;
              return currentPartIndex === listeningPartIds.length - 1;
            })())
        }
        onFinish={async () => {
          const studentAttemptId = location.state?.studentAttemptId;
          const isPracticeMode = location.state?.isPracticeMode;
          const isSimulationMode = !!location.state?.simulationStep;

          // Handle both practice mode and simulation mode
          if (isPracticeMode || isSimulationMode) {
            try {
              // Always submit answers (even if empty)
              let listeningTestId = testId;
              if (localListeningData?.data?.listeningTest?._id) {
                listeningTestId = localListeningData.data.listeningTest._id;
              }
              const getMcqOptionKey = (questionId, givenAnswer) => {
                console.log(
                  `[DEBUG onFinish] getMcqOptionKey called for questionId: ${questionId}, givenAnswer: "${givenAnswer}"`
                );

                // First search in transformed data structure
                const currentListeningData =
                  transformListeningData(localListeningData);
                const searchIn = [];
                if (currentListeningData?.partB?.extracts)
                  searchIn.push(...currentListeningData.partB.extracts);
                if (currentListeningData?.partC?.extracts)
                  searchIn.push(...currentListeningData.partC.extracts);

                console.log(
                  `[DEBUG onFinish] Searching in ${searchIn.length} extracts from transformed data`
                );

                for (const extract of searchIn) {
                  if (extract.questions) {
                    for (const qWrap of extract.questions) {
                      const q = qWrap.content;
                      if (
                        String(q.questionId) === String(questionId) &&
                        Array.isArray(q.options)
                      ) {
                        console.log(
                          `[DEBUG onFinish] Found question ${questionId} in transformed data with ${q.options.length} options`
                        );
                        console.log(
                          `[DEBUG onFinish] Question options:`,
                          q.options
                        );
                        const foundOption = q.options.find((opt, idx) => {
                          const optKey =
                            opt?.key ?? String.fromCharCode(65 + idx); // A, B, C, etc.
                          return (
                            opt.value === givenAnswer ||
                            opt.label === givenAnswer ||
                            opt.key === givenAnswer ||
                            optKey === givenAnswer ||
                            opt.text === givenAnswer
                          );
                        });
                        if (foundOption) {
                          console.log(
                            `[DEBUG onFinish] Found option:`,
                            foundOption,
                            `for answer: ${givenAnswer}`
                          );
                          // Return the option's key if it exists, otherwise return the original answer
                          const optionKey = foundOption.key || givenAnswer;
                          console.log(
                            `[DEBUG onFinish] Using option key: ${optionKey}`
                          );
                          return optionKey;
                        }
                      }
                    }
                  }
                }

                // Also search directly in API data structure for Part C
                const apiParts =
                  localListeningData?.data?.listeningTest?.listeningParts || [];
                console.log(
                  `[DEBUG onFinish] Searching in ${apiParts.length} API parts`
                );

                for (const part of apiParts) {
                  if (part.part === "C" && part.listeningExtract) {
                    console.log(
                      `[DEBUG onFinish] Found Part C with ${part.listeningExtract.length} extracts`
                    );
                    for (const extract of part.listeningExtract) {
                      if (extract.extractQuestion) {
                        console.log(
                          `[DEBUG onFinish] Extract has ${extract.extractQuestion.length} questions`
                        );
                        for (const question of extract.extractQuestion) {
                          if (
                            String(question.questionId) ===
                              String(questionId) &&
                            Array.isArray(question.options)
                          ) {
                            console.log(
                              `[DEBUG onFinish] Found Part C question ${questionId} with ${question.options.length} options`
                            );
                            console.log(
                              `[DEBUG onFinish] Part C question options:`,
                              question.options
                            );
                            const foundOption = question.options.find(
                              (opt, idx) => {
                                const optKey =
                                  opt?.key ?? String.fromCharCode(65 + idx); // A, B, C, etc.
                                return (
                                  opt.value === givenAnswer ||
                                  opt.label === givenAnswer ||
                                  opt.key === givenAnswer ||
                                  optKey === givenAnswer ||
                                  opt.text === givenAnswer
                                );
                              }
                            );
                            if (foundOption) {
                              console.log(
                                `[DEBUG onFinish] Found Part C option:`,
                                foundOption,
                                `for answer: ${givenAnswer}`
                              );
                              // Return the option's key if it exists, otherwise return the original answer
                              const optionKey = foundOption.key || givenAnswer;
                              console.log(
                                `[DEBUG onFinish] Using Part C option key: ${optionKey}`
                              );
                              return optionKey;
                            } else {
                              console.log(
                                `[DEBUG onFinish] No matching option found for answer: ${givenAnswer}, available options:`,
                                question.options
                              );
                            }
                          }
                        }
                      }
                    }
                  }
                }

                console.log(
                  `[DEBUG onFinish] No option key found, returning original answer: ${givenAnswer}`
                );
                return givenAnswer;
              };

              // Get all question IDs for the current part and build answers array
              const allQuestionIds = getAllQuestionIdsForCurrentPart();
              const submissionData = {
                testId: listeningTestId,
                testAttemptId: studentAttemptId,
                answers: allQuestionIds.map((questionId) => {
                  const givenAnswer =
                    answers[questionId] || answers[String(questionId)] || "";
                  return {
                    questionId: parseInt(questionId) || questionId,
                    givenAnswer: getMcqOptionKey(questionId, givenAnswer) || "",
                  };
                }),
                ...(studentAnswerId &&
                  typeof studentAnswerId === "string" &&
                  studentAnswerId.trim() !== "" && { studentAnswerId }),
                isNextPart: true,
              };
              console.log(
                "Submitting remaining answers on finish:",
                submissionData
              );
              await submitTestAnswers(submissionData).unwrap();

              // Mark test as completed and show results
              console.log(
                "Listening is final section, completing test and showing results"
              );
              await showTestResults(studentAttemptId, true);
            } catch (error) {
              console.error("Error in onFinish:", error);
              // If error, still try to show results
              await showTestResults(studentAttemptId, true);
            }
          }
        }}
      />
      {/* Results Popup for Practice Mode */}
      <TestResultsPopup
        open={showResultsPopup}
        onClose={() => setShowResultsPopup(false)}
        resultsData={testResults}
        onContinue={() => navigate(backRoute)}
        attemptId={studentAttemptId}
        mockTestId={testId}
      />
    </>
  );
}
